import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import * as clientSiteAttachmentService from "../../services/clientSiteAttachmentService";
import {
  ClientSiteAttachmentItem,
  ClientSiteAttachmentResponse,
  ClientSiteAttachmentCreatePayload,
} from "../../services/clientSiteAttachmentService";

interface ClientSiteAttachmentState {
  items: ClientSiteAttachmentItem[];
  loading: boolean;
  error: string | null;
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  // Track which sites have had their attachments loaded (even if empty)
  loadedSites: string[];
}

const initialState: ClientSiteAttachmentState = {
  items: [],
  loading: false,
  error: null,
  pageNumber: 1,
  pageSize: 100,
  totalCount: 0,
  totalPages: 0,
  loadedSites: [],
};

// Async thunk to fetch client site attachments
export const fetchClientSiteAttachments = createAsyncThunk(
  "clientSiteAttachments/fetchClientSiteAttachments",
  async ({
    clientSiteId,
    pageNumber = 1,
    pageSize = 100,
  }: {
    clientSiteId: string;
    pageNumber?: number;
    pageSize?: number;
  }) => {
    const response: ClientSiteAttachmentResponse =
      await clientSiteAttachmentService.getClientSiteAttachments(
        clientSiteId,
        pageNumber,
        pageSize
      );
    // Include clientSiteId in the response so the reducer knows which site's attachments to update
    return { ...response, clientSiteId };
  }
);

// Async thunk to upload a new attachment
export const uploadClientSiteAttachment = createAsyncThunk(
  "clientSiteAttachments/uploadClientSiteAttachment",
  async (payload: ClientSiteAttachmentCreatePayload) => {
    const response = await clientSiteAttachmentService.uploadClientSiteAttachment(payload);
    return response;
  }
);

// Async thunk to delete an attachment
export const deleteClientSiteAttachment = createAsyncThunk(
  "clientSiteAttachments/deleteClientSiteAttachment",
  async (attachmentId: string) => {
    await clientSiteAttachmentService.deleteClientSiteAttachment(attachmentId);
    return attachmentId;
  }
);

const clientSiteAttachmentSlice = createSlice({
  name: "ClientSiteAttachments",
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearAttachments(state) {
      state.items = [];
      state.totalCount = 0;
    },
    // Populate attachments from site object (when site is fetched with attachments)
    // IMPORTANT: This should be called even with empty arrays to signal that attachments have been loaded
    populateAttachmentsFromSite(state, action: PayloadAction<{ clientSiteId: string; attachments: ClientSiteAttachmentItem[] }>) {
      const payload = action.payload as { clientSiteId: string; attachments: ClientSiteAttachmentItem[] };
      const clientSiteId = payload.clientSiteId;
      const attachments: ClientSiteAttachmentItem[] = Array.isArray(payload.attachments) ? payload.attachments : [];
      if (Array.isArray(payload.attachments)) {
        // Filter out deleted attachments (even if empty array, this ensures we mark attachments as loaded)
        const validAttachments = attachments.filter(att => !att.isDeleted);
        // Replace all attachments for this site with the ones from the site object
        // Remove existing attachments for this site
        state.items = state.items.filter(item => item.clientSiteId !== clientSiteId);
        // Add new attachments from site object, ensuring clientSiteId is set for filtering
        const itemsWithSiteId = validAttachments.map(att => ({ ...att, clientSiteId }));
        state.items = [...state.items, ...itemsWithSiteId];
        // Mark this site as having loaded attachments (even if empty)
        // Always update the loadedSites array to ensure it's marked as loaded
        if (!state.loadedSites.includes(clientSiteId)) {
          state.loadedSites.push(clientSiteId);
        }
        // Update total count for this site (same as items we added)
        state.totalCount = validAttachments.length;
      } else {
        // Handle empty array or null - mark as loaded but with no attachments
        // Remove existing attachments for this site
        state.items = state.items.filter(item => item.clientSiteId !== clientSiteId);
        // Mark this site as having loaded attachments (even if empty)
        if (!state.loadedSites.includes(clientSiteId)) {
          state.loadedSites.push(clientSiteId);
        }
        // Update total count for this site
        state.totalCount = 0;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch attachments
      .addCase(fetchClientSiteAttachments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientSiteAttachments.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (action.payload && action.payload.items && Array.isArray(action.payload.items)) {
          // Get clientSiteId from the response (we added it in the thunk) or from first item
          const clientSiteId = (action.payload as any).clientSiteId || 
                               (action.payload.items.length > 0 ? action.payload.items[0].clientSiteId : null);
          
          if (clientSiteId) {
            // Replace only attachments for THIS site, keep attachments for other sites
            const validItems = action.payload.items.filter((item) => !item.isDeleted);
            // Remove old attachments for this site
            state.items = state.items.filter((item) => item.clientSiteId !== clientSiteId);
            // Add new attachments for this site
            state.items = [...state.items, ...validItems];
            // Mark this site as having loaded attachments
            if (!state.loadedSites.includes(clientSiteId)) {
              state.loadedSites.push(clientSiteId);
            }
          } else {
            // Fallback: if no clientSiteId, replace all (shouldn't happen but handle gracefully)
            state.items = action.payload.items.filter((item) => !item.isDeleted);
          }
          
          state.pageNumber = action.payload.pageNumber || 1;
          state.pageSize = action.payload.pageSize || 100;
          state.totalCount = action.payload.totalCount || 0;
          state.totalPages = action.payload.totalPages || 0;
        } else {
          state.items = [];
          state.pageNumber = action.payload?.pageNumber || 1;
          state.pageSize = action.payload?.pageSize || 100;
          state.totalCount = action.payload?.totalCount || 0;
          state.totalPages = action.payload?.totalPages || 0;
        }
      })
      .addCase(fetchClientSiteAttachments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch attachments";
      })
      // Upload attachment
      .addCase(uploadClientSiteAttachment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadClientSiteAttachment.fulfilled, (state, action) => {
        state.loading = false;
        state.items.unshift(action.payload);
        state.totalCount += 1;
      })
      .addCase(uploadClientSiteAttachment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to upload attachment";
      })
      // Delete attachment
      .addCase(deleteClientSiteAttachment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteClientSiteAttachment.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((item) => item.id !== action.payload);
        state.totalCount -= 1;
      })
      .addCase(deleteClientSiteAttachment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete attachment";
      });
  },
});

export const { clearError, clearAttachments, populateAttachmentsFromSite } = clientSiteAttachmentSlice.actions;
export default clientSiteAttachmentSlice.reducer;

// Selectors
export const selectClientSiteAttachmentList = (state: any) =>
  state.ClientSiteAttachments?.items || [];
export const selectClientSiteAttachmentLoading = (state: any) =>
  state.ClientSiteAttachments?.loading || false;
export const selectClientSiteAttachmentError = (state: any) =>
  state.ClientSiteAttachments?.error || null;
export const selectClientSiteAttachmentTotalCount = (state: any) =>
  state.ClientSiteAttachments?.totalCount || 0;

// Memoized selector to filter attachments by clientSiteId
const selectAllAttachments = (state: any) => state.ClientSiteAttachments?.items || [];
const selectLoadedSites = (state: any) => state.ClientSiteAttachments?.loadedSites || [];

export const selectClientSiteAttachmentsByClientSiteId = createSelector(
  [selectAllAttachments, (_state: any, clientSiteId: string) => clientSiteId],
  (items: ClientSiteAttachmentItem[], clientSiteId: string) => {
    return items.filter((item: ClientSiteAttachmentItem) => item.clientSiteId === clientSiteId);
  }
);

// Selector to check if attachments have been loaded for a site (even if empty)
export const selectAreAttachmentsLoadedForSite = createSelector(
  [selectLoadedSites, (_state: any, clientSiteId: string) => clientSiteId],
  (loadedSites: string[], clientSiteId: string) => {
    return loadedSites.includes(clientSiteId);
  }
);

